import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import { AmountTypeApiValues } from '@/scopes/payment/infrastructure/types'
import { PaymentMethodRow } from '../../policies/paymentMethodsView'
import {
  ADYEN_CARD_CONFIG_KEY,
  ADYEN_CREDIT_CARD_GROUP_CODE,
  AmountTypeValues,
  type AmountWithFee,
  CREDIT_CARD_PAYMENT_METHOD_KEY,
  PaymentAmount,
  PaymentMethodOption,
  PaymentOptions,
} from '../../types'

/**
 * Determines if a custom deposit amount is truly custom.
 * A custom amount is only considered "selected" if it's not null AND differs from the standard amount.
 */
function isCustomDepositAmountSelected(customDepositAmount: number | null, standardAmount: number | null): boolean {
  return customDepositAmount !== null && customDepositAmount !== standardAmount
}

export function getSelectedAmount(paymentAmounts: PaymentAmount[], selectedAmountType: string): PaymentAmount | null {
  return paymentAmounts.find(amount => amount.type === selectedAmountType) ?? null
}

export function getAmountValue(amount: PaymentAmount | null, customDepositAmount: number | null): number | null {
  if (!amount) {
    return null
  }

  if (amount.type === 'deposit' && customDepositAmount !== null) {
    return customDepositAmount
  }

  return amount.amount
}

export function getAmountWithFeesValue(
  amount: PaymentAmount | null,
  customDepositAmount: number | null,
  paymentMethodType?: PaymentMethod | null,
): number | null {
  const amountValue = getAmountValue(amount, customDepositAmount)

  if (!amount || amountValue === null || !paymentMethodType || isCustomDepositAmountSelected(customDepositAmount, amount.amount)) {
    return amountValue
  }

  return getPaymentMethodFee(amount, paymentMethodType)?.amount ?? amountValue
}

export function getPaymentMethodFee(
  amount: PaymentAmount | null,
  paymentMethodType: PaymentMethod,
): AmountWithFee | null {
  return amount?.amountWithFees?.find(v => v.paymentMethodType.toLowerCase() === paymentMethodType.toLowerCase()) ?? null
}

export function resolvePaymentAmounts(
  selectedAmount: PaymentAmount | null,
  customDepositAmount: number | null,
  allowsCustomAmount?: boolean,
  paymentMethodType?: PaymentMethod | null,
  isGroupBooking?: boolean,
): {
    fullPaymentAmount: number | null
    downPaymentAmount: number | null
    partialPaymentAmount: number | null
  } {
  if (!selectedAmount) {
    return {
      fullPaymentAmount: null,
      downPaymentAmount: null,
      partialPaymentAmount: null,
    }
  }

  const amountValue = getAmountWithFeesValue(selectedAmount, customDepositAmount, paymentMethodType)

  if (selectedAmount.type === AmountTypeValues.FULL) {
    return {
      fullPaymentAmount: isGroupBooking ? null : amountValue,
      downPaymentAmount: null,
      partialPaymentAmount: null,
    }
  }

  if (selectedAmount.type === AmountTypeValues.DEPOSIT && allowsCustomAmount) {
    return {
      fullPaymentAmount: null,
      downPaymentAmount: null,
      partialPaymentAmount: amountValue,
    }
  }

  if (selectedAmount.type === AmountTypeValues.DEPOSIT) {
    return {
      fullPaymentAmount: null,
      downPaymentAmount: isGroupBooking ? null : amountValue,
      partialPaymentAmount: null,
    }
  }

  if (selectedAmount.type === AmountTypeValues.MIN_DEPOSIT) {
    return {
      fullPaymentAmount: null,
      downPaymentAmount: null,
      partialPaymentAmount: amountValue,
    }
  }

  return {
    fullPaymentAmount: null,
    downPaymentAmount: null,
    partialPaymentAmount: null,
  }
}

export function getPaymentMethodType(paymentMethodRow: PaymentMethodRow): string {
  return paymentMethodRow.key === CREDIT_CARD_PAYMENT_METHOD_KEY ? ADYEN_CREDIT_CARD_GROUP_CODE : paymentMethodRow.key
}

export function getPaymentMethodConfigKey(paymentMethodRow: PaymentMethodRow): string {
  return paymentMethodRow.key === CREDIT_CARD_PAYMENT_METHOD_KEY ? ADYEN_CARD_CONFIG_KEY : paymentMethodRow.key
}

export function resolveSelectedPaymentMethod(
  paymentOptions: PaymentOptions,
  selectedPaymentMethodType: PaymentMethod | null,
): PaymentMethodOption | null {
  if (!selectedPaymentMethodType) {
    return null
  }

  if (selectedPaymentMethodType === CREDIT_CARD_PAYMENT_METHOD_KEY) {
    return paymentOptions.paymentMethods.find(method => method.groupCode === ADYEN_CREDIT_CARD_GROUP_CODE) ?? null
  }

  return paymentOptions.paymentMethods.find(method => method.paymentMethodType === selectedPaymentMethodType) ?? null
}

export function resolvePaymentMethodType(type: PaymentMethod | null) {
  if (!type) {
    return null
  }

  if (type === CREDIT_CARD_PAYMENT_METHOD_KEY) {
    return ADYEN_CREDIT_CARD_GROUP_CODE
  }

  return type
}

export function resolveSelectedAmountType(
  selectedAmountType: string,
  allowsCustomAmount: boolean,
): string {
  if (!selectedAmountType) {
    return ''
  }
  if (selectedAmountType === AmountTypeValues.DEPOSIT && allowsCustomAmount) {
    return AmountTypeApiValues.CUSTOM_PARTIAL
  }

  if (selectedAmountType === AmountTypeValues.DEPOSIT) {
    return AmountTypeApiValues.PARTIAL
  }

  if (selectedAmountType === AmountTypeValues.MIN_DEPOSIT) {
    return AmountTypeApiValues.MIN_DEPOSIT
  }

  return AmountTypeApiValues.FULL
}
